import _ from 'lodash';
import React, { Component, Fragment } from 'react';
import get from 'lodash/get';
import * as Sentry from '@sentry/browser';
import { Link, browserHistory, withRouter } from 'react-router';

import Loading from 'components/Loading/Loading';
import StripeDefaultCardSelector from 'selectors/StripeDefaultCardSelector';
import PaymentForm from 'components/forms/PaymentForm';
import SecuredCheckout from 'components/SecuredCheckout';
import PurchaseTotal from 'components/PurchaseTotal';
import PromoCodeSpot from 'components/PromoCodeSpot';
import SelectPlan from 'components/PlanSelect/SelectPlan';
import AnnualCommitment from 'components/PlanSelect/AnnualCommitment';
import MonthlyCommitment from 'components/PlanSelect/MonthlyCommitment';

import { compose } from 'redux';
import { connect } from 'react-redux';
import { SubmissionError } from 'redux-form';
import { injectStripe } from 'react-stripe-elements';

import { addStripeCard, addStripeCardAsync } from 'actions/stripeCardActions';
import { createMembership } from 'actions/stripeSubscriptionActions';
import { makePayment } from 'actions/stripePaymentActions';
import {
  fetchStripeCustomer,
  createStripeCustomerAsync,
} from 'actions/stripeCustomerActions';
import { createReservation } from 'actions/reservationActions';
import { signupAsync, updateUpsellData } from 'actions/userActions';

import {
  getPlan, getUpsellPlan, getCommitmentGroup, getPriceSubText
} from 'utils/stripePlanUtils';
import { billingCycleQuery, planPartsFromQuery } from 'utils/planQueryString';
import { overwriteQueryParam } from 'utils/urlUtils';

import { PLAN_CATEGORY, DEFAULT_BILLING_CYCLE } from 'config/constants';

import '../ActivateSubscription/style.css';

class ActivateSubscriptionV2 extends Component {
  constructor(props) {
    super(props);
    this.priceSubText = getPriceSubText('activate');
    this.state = {
      billingCycle: DEFAULT_BILLING_CYCLE,
      showCycleSelect: true,
      coupon: null,
      error: null,
      couponStatus: 'none',
      isActivating: false,
      editCreditCard: false,
      cardInfoChanged: null,
      email: get(this.props, 'location.query.email', ''),
      src: get(this.props, 'location.query.src', ''),
    };
  }

  componentDidMount() {
    this.getPlanFromQuery();
    // May need to address this in prevProps as well
    if (_.isEmpty(this.props.stripeCustomer) && this.props.user.profile) {
      this.props.fetchStripeCustomer(this.props.user.profile.stripeCustomerId);
      // if (this.props.user.profile) {
      //   this.props.fetchStripeCustomer(
      //     this.props.user.profile.stripeCustomerId
      //   );
      // } else {
      //   browserHistory.push("/activate");
      // }
    }
  }

  getPlanFromQuery = () => {
    const { billingCycle } = planPartsFromQuery(
      this.props.location.query
    );
    this.handleSelectCycle(billingCycle);
  };

  handleSeeAllPlans = (event) => {
    event.preventDefault();
    this.setState({ showCycleSelect: true });
  };

  handleSelectCycle = (billingCycle) => {
    this.setState({ billingCycle });
    this.modifyBrowserHistory(billingCycle);
  };

  isLoggedIn = () => {
    return (
      this.props.user &&
      (this.props.user.reauthenticateStatus === 'SUCCESS' ||
        this.props.user.loginStatus === 'SUCCESS')
    );
  };

  login = async () => {
    const { email, name } = this.state;
    try {
      const response = await this.props.signupAsync(email, name, null);
      return response.account;
    } catch (err) {
      Sentry.captureMessage(
        `Login on activate page failed for email: ${email}.`,
        err
      );
      throw new SubmissionError(err);
    }
  };

  getStripeToken = async () => {
    const { user, stripe } = this.props;
    const tokenResponse = await stripe.createToken();
    const { token } = tokenResponse;
    if (!token) {
      Sentry.captureMessage(
        `Stripe token creation failed for email: ${user.profile.email}`
      );
      // TODO: show error
      return null;
    }
    return token;
  };

  getOrCreateStripeCustomer = async (user) => {
    const email = get(user, 'profile.email');
    const stripeCustomerId = get(user, 'profile.stripeCustomerId');
    if (stripeCustomerId) return stripeCustomerId;
    const stripeCustomerResponse = await this.props.createStripeCustomerAsync(
      email
    );
    return stripeCustomerResponse.stripeCustomerId;
  };

  checkPlanForUpselling = (planId) => {
    const plan = getUpsellPlan(planId);
    this.props.updateUpsellData({ upsellPlan: plan });
  };

  handleActivate = async () => {
    this.setState({
      isActivating: true,
      error: null,
    });

    try {
      if (!this.isLoggedIn()) {
        await this.login();
      }

      const { bookingData, user } = this.props;
      const stripeCustomerId = await this.getOrCreateStripeCustomer(user);
      if (!stripeCustomerId) {
        this.setState({
          isActivating: false,
          error: 'Internal payment issue, please try again later.',
        });
        Sentry.captureMessage(
          `Could not get or create stripe customer for email: ${user.email}`
        );
        return;
      }

      if (this.state.cardInfoChanged) {
        // TODO: what if card changes but the number stays the name?
        // console.log("Change object", this.state.cardInfoChanged);
        const token = await this.getStripeToken();
        if (!token) {
          this.setState({
            isActivating: false,
            error: 'Internal payment issue, please try again later.',
          });
          return;
        }
        await this.addCard(stripeCustomerId, token);
      }

      await this.createSubscription(stripeCustomerId);

      if (bookingData) {
        this.props.createReservation(bookingData, false, null, () => {
          if (user.profile.activate) {
            browserHistory.push('reservations');
          } else {
            browserHistory.push('signup-details');
          }
        });
      } else if (user.profile.activate) {
        browserHistory.push('schedule-lesson');
      } else {
        browserHistory.push('signup-details');
      }
    } catch (ex) {
      console.error(ex);
      this.setState({
        isActivating: false,
        error: 'Unexpected error',
      });
    }
  };

  addCard = async (stripeCustomerId, token) => {
    await this.props.addStripeCardAsync(stripeCustomerId, token.id, false);
  };

  createSubscription = (stripeCustomerId) => {
    const { coupon, billingCycle } = this.state;
    const plan = getPlan(billingCycle);
    const data = {
      stripeCustomerId,
      planId: plan.id,
      category: PLAN_CATEGORY.LANGUAGE,
      src: this.state.src,
    };

    if (coupon && coupon.valid) {
      data.coupon = coupon.id;
    }

    return new Promise((resolve, reject) => {
      this.props.createMembership(
        data,
        () => {
          this.checkPlanForUpselling(plan.id);
          resolve();
        },
        (response) => {
          reject();
        }
      );
    });
  };

  onApplyPromoCode = (promo) => {
    this.setState({ coupon: promo });
  };

  onClearPromoCode = () => {
    this.setState({ coupon: null });
  };

  onPlanSelect=(availablePlan) => {
    this.modifyBrowserHistory(availablePlan.cycle);
    this.setState({ billingCycle: availablePlan.cycle });
  }

  modifyBrowserHistory(billingCycle) {
    const query = overwriteQueryParam(
      window.location.search,
      'plan',
      billingCycleQuery(billingCycle)
    );
    browserHistory.replace('/activate?' + query);
  }

  renderPaymentMethod() {
    const { defaultCard } = this.props;
    const name = defaultCard ?
      `Visa ending in ${defaultCard.last4}` :
      'No active payment method';
    const buttonText = defaultCard ? 'Edit' : 'Add';

    return this.state.editCreditCard || !defaultCard ? (
      <PaymentForm
        hidePostalCode
        hasCards
        hideFieldName
        onChange={(changeObject) => this.setState({
          cardInfoChanged: changeObject,
        })}
      />
    ) : (
      <Fragment>
        {name}
        <Link
          className="d-block"
          onClick={() => {
            this.setState({
              editCreditCard: true,
            });
          }}
        >
          {buttonText}
        </Link>
      </Fragment>
    );
  }

  // TO-DO , REMOVE method for next release.
  renderMonthlyPriceHeader(selectedPlan) {
    if (selectedPlan.isPrepaid) {
      if (this.state.coupon) {
        const monthlyCents = selectedPlan.price.month * 100;
        const newMonthlyCents = monthlyCents - this.state.coupon.amount_off / 6;
        const formatter = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        });
        const newMonthly = formatter.format(newMonthlyCents / 100);
        return (
          <Fragment>
            <div style={{ textAlign: 'center' }}>
              <span style={{ textDecoration: 'line-through' }}>
                &nbsp;${selectedPlan.price.month}/mo
              </span>
            </div>
            <div className="activate-content__sub-title">
              <strong>
                <span style={{ fontSize: 16, marginRight: 4 }}>
                  {newMonthly}/mo
                </span>
                <span style={{ fontSize: 14 }}>(prepaid 6 months)</span>
              </strong>
            </div>
          </Fragment>
        );
      }
      return (
        <div className="activate-content__sub-title">
          <span style={{ fontSize: 18 }}>
            {' '}
            <b>${selectedPlan.price.month}/mo </b>
          </span>
          <span style={{ fontSize: 14 }}>(prepaid 6 months)</span>
        </div>
      );
    }
    return (
      <div className="activate-content__sub-title">
        <span style={{ fontSize: 18 }}>
          {' '}
          <b>${selectedPlan.price.month}/mo</b>
        </span>
      </div>
    );
  }

  render() {
    const {
      user, stripeCards, payment, subscriptionError
    } = this.props;
    const { billingCycle } = this.state;

    let error = null;
    if (subscriptionError) {
      error = get(subscriptionError, 'message');
    } else if (stripeCards.edit_error) {
      error = get(stripeCards, 'edit_error');
    } else if (payment.error) {
      error = payment.error;
    } else if (user && user.error && user.error.message) {
      error = user.error.message;
    } else {
      error = this.state.error;
    }
    // ensure `getPlan` returns a selected plan
    const selectedPlan = getPlan(billingCycle);
    const commitmentGroup = getCommitmentGroup(selectedPlan);
    const annualCommitmentPlan = commitmentGroup.find(cGroup => cGroup.isAnnual);
    const monthlyCommitmentPlan = commitmentGroup.find(cGroup => !cGroup.isAnnual);
    const totalCost = selectedPlan.price.month;
    return (
      <div className="activate-content mx-auto my-5">
        <div className="row d-block">
          <div className="col activate-ms-content--wrapper">
            <div className="activate-ms-content activate-ms-content--activate text-center">
              <section className="activate-ms-content__section activate-ms-content__section--billing text-left">
                <div className="activate-content__title-wrapper">
                  <h2 className="activate-content__title">Start learning with</h2>
                  <h2 className="activate-content__title">your private tutor</h2>
                </div>
                <div className="activate-content__general-explanation">
                  <p className="activate-content__general-explanation__title">Choose your weekly schedule</p>
                  <p className="activate-content__general-explanation__sub-title">All 1-on-1 lessons are 30 minutes each.</p>
                </div>
                <SelectPlan
                  commitmentGroup={commitmentGroup}
                  selectedPlan={selectedPlan}
                  onPlanSelect={this.onPlanSelect}
                >
                  <AnnualCommitment
                    priceSubText={this.priceSubText}
                    availablePlan={selectedPlan}
                    annualPlan={annualCommitmentPlan}
                    onPlanSelect={this.onPlanSelect}
                  />
                  <MonthlyCommitment
                    availablePlan={selectedPlan}
                    monthlyPlan={monthlyCommitmentPlan}
                    onPlanSelect={this.onPlanSelect}
                  />
                </SelectPlan>
                {!this.isLoggedIn() && (
                  <Fragment>
                    <div className="row">
                      <div className="form-group col-12 mb-4">
                        <label className="form-control-label" htmlFor="email">
                          Email
                        </label>
                        <input
                          name="email"
                          className="form-control"
                          type="text"
                          placeholder="abc@gmail.com"
                          value={this.state.email}
                          onChange={(e) => this.setState({
                            email: e.target.value,
                          })}
                        />
                      </div>
                    </div>
                  </Fragment>
                )}
                <div className="activate-ms-content__tooltip-wrapper">
                  <p className="form-control-label m-0">Payment with</p>
                </div>

                <div className="mb-2">{this.renderPaymentMethod()}</div>

                <PurchaseTotal
                  promo={this.state.coupon}
                  originalCents={totalCost * 100}
                  selectedPlan={selectedPlan}
                />

                <p className="activate-ms-content__section-desc my-3 font-weight-normal">
                  By clicking the button below you agree to our{' '}
                  <a
                    href="https://www.rypeapp.com/terms-of-use"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms of Service
                  </a>
                </p>
              </section>
              <div>
                {this.state.isActivating ? (
                  <Loading size="small" />
                ) : (
                  <Fragment>
                    <button
                      type="button"
                      className="activate-ms-content__submit-btn btn btn-primary btn-cursor w-100"
                      onClick={this.handleActivate}
                    >
                      Confirm and Pay
                    </button>
                    {error && (
                      <p className="text-danger my-1">
                        <small>{error}</small>
                      </p>
                    )}
                  </Fragment>
                )}
              </div>
              <SecuredCheckout className="activate-ms-content__secure-text" />
            </div>

            <div className="activate-ms-content activate-ms-content--benefits">
              <div className="activate-ms-content__promo">
                <PromoCodeSpot
                  initialPromoCode={this.props.location.query.promo}
                  onApplyPromoCode={this.onApplyPromoCode}
                  onClearPromoCode={this.onClearPromoCode}
                  checkPlan={selectedPlan}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    user: state.user,
    bookingData: state.user.bookingData,
    stripeCards: state.stripeCards,
    subscriptionError: state.subscription.error,
    payment: state.stripePayment,
    defaultCard: StripeDefaultCardSelector(state),
  };
}

export default compose(
  withRouter,
  injectStripe,
  connect(mapStateToProps, {
    addStripeCard,
    addStripeCardAsync,
    createMembership,
    createReservation,
    fetchStripeCustomer,
    createStripeCustomerAsync,
    makePayment,
    updateUpsellData,
    signupAsync,
  })
)(ActivateSubscriptionV2);



// WEBPACK FOOTER //
// ./src/pages/student/Activate/ActivateSubscriptionV2/index.js